import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDay, daysBetween } from "@/lib/format";
import {
  Plus,
  CheckCircle2,
  XCircle,
  Activity,
  Search,
  Layers,
  LayoutGrid,
  Loader2,
} from "lucide-react";
import NewDealModal from "@/components/NewDealModal";
import MoveStageModal from "@/components/MoveStageModal";
import { usePipelineCtx } from "@/lib/pipelineContext.jsx";
import { useTerm } from "@/lib/terminology.jsx";
import { SkeletonRows } from "@/components/Skeleton";

const TABS = [
  { key: "active", label: "Active Pipeline", icon: Activity },
  { key: "accepted", label: "Accepted Deals", icon: CheckCircle2 },
  { key: "rejected", label: "Rejected Deals", icon: XCircle },
];

const COLUMN_BATCH = 20; // CW6.2.7
const FLAT_BATCH = 50;   // for Accepted/Rejected list tabs

function translateLabel(label, term) {
  if (!label) return label;
  return label.split(/(\s+)/).map((tok) => (/^\s+$/.test(tok) ? tok : term(tok))).join("");
}

export default function Pipeline() {
  const nav = useNavigate();
  const term = useTerm();
  const { pipelines, pipelineId } = usePipelineCtx();
  const [stages, setStages] = useState([]);
  // CW6.2.7: per-stage paginated columns in active tab
  const [columnData, setColumnData] = useState({}); // { stage_id: { items, offset, hasMore, loading, error } }
  const [stageCounts, setStageCounts] = useState({}); // { stage_id: count }
  // flat lists for Accepted/Rejected
  const [flatDeals, setFlatDeals] = useState([]);
  const [flatOffset, setFlatOffset] = useState(0);
  const [flatHasMore, setFlatHasMore] = useState(true);
  const [flatLoading, setFlatLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [activeTab, setActiveTab] = useState("active");
  const [showNewDeal, setShowNewDeal] = useState(false);
  const [moveDeal, setMoveDeal] = useState(null); // {deal, toStage}
  const [search, setSearch] = useState("");
  // v5.8 A12: filter by tag
  const [tagId, setTagId] = useState("");
  const [pipelineTags, setPipelineTags] = useState([]);
  // v5.7 Addition 11: Kanban view mode
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("cw_kanban_view") || "detailed"
  );

  // CW6.2.7: scroll-position restore when returning via back nav
  const scrollKey = `cw_pipeline_scroll_${pipelineId || ""}_${activeTab}`;
  useEffect(() => {
    const saved = parseInt(sessionStorage.getItem(scrollKey) || "0", 10);
    if (saved > 0) window.scrollTo(0, saved);
    const onScroll = () => sessionStorage.setItem(scrollKey, String(window.scrollY));
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [scrollKey]);

  // Load stages + tags on pipeline change
  useEffect(() => {
    if (!pipelineId) return;
    api.get(`/pipelines/${pipelineId}/stages`).then(({ data }) => setStages(data));
    api.get(`/tags`, { params: { pipeline_id: pipelineId } })
      .then(({ data }) => setPipelineTags(data)).catch(() => setPipelineTags([]));
    refreshSummary();
  }, [pipelineId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Server-side filter params (search / tag / status)
  const filterParams = useCallback(() => {
    const p = { pipeline_id: pipelineId, status_filter: activeTab };
    if (search.trim()) p.q = search.trim();
    if (tagId) p.tag_id = tagId;
    return p;
  }, [pipelineId, activeTab, search, tagId]);

  // Load column counts for the active tab
  const loadStageCounts = useCallback(async () => {
    if (!pipelineId) return;
    try {
      const { data } = await api.get("/deals/stage-counts", { params: filterParams() });
      setStageCounts(data.by_stage || {});
    } catch (_e) { /* non-fatal */ }
  }, [pipelineId, filterParams]);

  // Load initial batch for every column in parallel
  const loadAllColumns = useCallback(async () => {
    if (!pipelineId || stages.length === 0) return;
    // reset state
    const empty = {};
    stages.forEach((s) => {
      empty[s.stage_id] = { items: [], offset: 0, hasMore: true, loading: true, error: null };
    });
    setColumnData(empty);

    const results = await Promise.all(
      stages.map(async (s) => {
        try {
          const { data } = await api.get("/deals", {
            params: { ...filterParams(), stage_id: s.stage_id, limit: COLUMN_BATCH, offset: 0 },
          });
          return [s.stage_id, { items: data, offset: data.length, hasMore: data.length === COLUMN_BATCH, loading: false, error: null }];
        } catch (err) {
          return [s.stage_id, { items: [], offset: 0, hasMore: false, loading: false, error: err?.response?.data?.detail || "Failed" }];
        }
      })
    );
    setColumnData(Object.fromEntries(results));
  }, [pipelineId, stages, filterParams]);

  // Load more for a specific column
  const loadMoreColumn = useCallback(async (stage_id) => {
    const col = columnData[stage_id];
    if (!col || col.loading || !col.hasMore) return;
    setColumnData((prev) => ({ ...prev, [stage_id]: { ...prev[stage_id], loading: true } }));
    try {
      const { data } = await api.get("/deals", {
        params: { ...filterParams(), stage_id, limit: COLUMN_BATCH, offset: col.offset },
      });
      setColumnData((prev) => ({
        ...prev,
        [stage_id]: {
          items: [...prev[stage_id].items, ...data],
          offset: prev[stage_id].offset + data.length,
          hasMore: data.length === COLUMN_BATCH,
          loading: false,
          error: null,
        },
      }));
    } catch (err) {
      setColumnData((prev) => ({
        ...prev,
        [stage_id]: { ...prev[stage_id], loading: false, error: err?.response?.data?.detail || "Failed" },
      }));
    }
  }, [columnData, filterParams]);

  // Load flat list for Accepted/Rejected
  const loadFlat = useCallback(async (append = false) => {
    if (!pipelineId) return;
    setFlatLoading(true);
    try {
      const offset = append ? flatOffset : 0;
      const { data } = await api.get("/deals", {
        params: { ...filterParams(), limit: FLAT_BATCH, offset },
      });
      setFlatDeals((prev) => (append ? [...prev, ...data] : data));
      setFlatOffset(offset + data.length);
      setFlatHasMore(data.length === FLAT_BATCH);
    } catch (_e) { /* ignore */ }
    finally { setFlatLoading(false); }
  }, [pipelineId, filterParams, flatOffset]);

  // Trigger loads when filters/tab change
  useEffect(() => {
    if (!pipelineId) return;
    if (activeTab === "active") {
      loadAllColumns();
      loadStageCounts();
    } else {
      setFlatDeals([]);
      setFlatOffset(0);
      setFlatHasMore(true);
      loadFlat(false);
      loadStageCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pipelineId, activeTab, search, tagId, stages.length]);

  useEffect(() => {
    localStorage.setItem("cw_kanban_view", viewMode);
  }, [viewMode]);

  const refreshSummary = () => {
    if (!pipelineId) return;
    api
      .get(`/deals/summary`, { params: { pipeline_id: pipelineId } })
      .then(({ data }) => setSummary(data));
  };

  // Compat shim: existing modals call refreshDeals() after create.
  const refreshDeals = () => {
    if (activeTab === "active") {
      loadAllColumns();
      loadStageCounts();
    } else {
      loadFlat(false);
    }
  };

  // Drag & drop
  const onDragStart = (e, deal) => {
    e.dataTransfer.setData("dealId", deal.deal_id);
    e.dataTransfer.effectAllowed = "move";
  };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (e, stageId) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData("dealId");
    // CW6.2.7: look up the deal from any loaded column (partial columns OK)
    let deal = null;
    for (const sid of Object.keys(columnData)) {
      deal = (columnData[sid]?.items || []).find((d) => d.deal_id === dealId);
      if (deal) break;
    }
    const toStage = stages.find((s) => s.stage_id === stageId);
    if (!deal || !toStage) return;
    if (deal.current_stage_id === stageId) return;
    setMoveDeal({ deal, toStage });
  };

  const submitMove = async (comment) => {
    if (!moveDeal) return;
    try {
      await api.post(`/deals/${moveDeal.deal.deal_id}/move-stage`, {
        to_stage_id: moveDeal.toStage.stage_id,
        comment,
      });
      toast.success(`Moved ${moveDeal.deal.company_name} to ${moveDeal.toStage.stage_name}`);
      setMoveDeal(null);
      refreshDeals();
      refreshSummary();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to move deal");
    }
  };

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="cw-caption uppercase tracking-widest">{term("Pipeline")}</div>
          <h1 className="cw-h1 mt-1" data-testid="pipeline-title">
            {pipelines.find((p) => p.pipeline_id === pipelineId)?.pipeline_name || term("Pipeline")}
          </h1>
          <p className="text-sm text-[var(--cw-muted)] mt-1">
            Kanban board · drag {term("deals")} across {term("stages")} · click a card to open detail
          </p>
        </div>
        <NewDealDropdown pipelineId={pipelineId} onManual={() => setShowNewDeal(true)} />
      </div>

      {/* v5.5 Addition 17: Search + v5.8 A12: Tag filter */}
      <div className="mt-4 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search
            size={14}
            className="absolute top-1/2 -translate-y-1/2 left-3"
            style={{ color: "var(--cw-muted)" }}
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={`Search company, domain, or ${term("founder").toLowerCase()}…`}
            className="w-full h-9 pl-9 pr-3 rounded-md border text-sm"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="deal-search-input"
          />
        </div>
        {pipelineTags.length > 0 && (
          <select
            value={tagId}
            onChange={(e) => setTagId(e.target.value)}
            className="h-9 px-3 rounded-md border text-sm bg-white"
            style={{ borderColor: "var(--cw-border)" }}
            data-testid="pipeline-tag-filter"
          >
            <option value="">All tags</option>
            {pipelineTags.map((t) => (
              <option key={t.tag_id} value={t.tag_id}>{t.tag_name}</option>
            ))}
          </select>
        )}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-6">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`cw-card p-4 text-left transition ${
              activeTab === t.key ? "ring-2" : ""
            }`}
            style={
              activeTab === t.key
                ? { borderColor: "var(--cw-primary)", boxShadow: "0 0 0 2px var(--cw-primary)" }
                : {}
            }
            data-testid={`metric-${t.key}`}
          >
            <div className="flex items-center gap-2 cw-caption uppercase tracking-wide">
              <t.icon size={14} />
              {translateLabel(t.label, term)}
            </div>
            <div className="text-2xl font-bold mt-1" style={{ color: "var(--cw-primary-dark)" }}>
              {summary[t.key] ?? 0}
            </div>
          </button>
        ))}
      </div>

      {/* Tab bar */}
      <div className="mt-6 border-b flex gap-1" style={{ borderColor: "var(--cw-border)" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setActiveTab(t.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
              activeTab === t.key ? "" : "text-[var(--cw-muted)] border-transparent"
            }`}
            style={
              activeTab === t.key
                ? { color: "var(--cw-primary)", borderColor: "var(--cw-secondary)" }
                : {}
            }
            data-testid={`tab-${t.key}`}
          >
            {translateLabel(t.label, term)}
          </button>
        ))}
      </div>

      {/* Body */}
      {activeTab === "active" ? (
        <>
          {/* v5.7 Addition 11: Kanban view toggle */}
          <div className="mt-6 flex items-center gap-2" data-testid="kanban-view-toggle">
            <span className="text-xs uppercase tracking-widest text-[var(--cw-muted)]">View</span>
            <div
              className="inline-flex rounded-md border p-0.5"
              style={{ borderColor: "var(--cw-border)" }}
            >
              <button
                onClick={() => setViewMode("detailed")}
                data-testid="kanban-view-detailed"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition"
                style={
                  viewMode === "detailed"
                    ? { background: "var(--cw-primary)", color: "white" }
                    : { color: "var(--cw-muted)" }
                }
              >
                <Layers size={12} /> Detailed
              </button>
              <button
                onClick={() => setViewMode("quick")}
                data-testid="kanban-view-quick"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded transition"
                style={
                  viewMode === "quick"
                    ? { background: "var(--cw-primary)", color: "white" }
                    : { color: "var(--cw-muted)" }
                }
              >
                <LayoutGrid size={12} /> Quick
              </button>
            </div>
          </div>
          <KanbanBoard
            stages={stages}
            columnData={columnData}
            stageCounts={stageCounts}
            onLoadMoreColumn={loadMoreColumn}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDrop={onDrop}
            onOpenDeal={(d) => nav(`/deals/${d.deal_id}`)}
            viewMode={viewMode}
          />
        </>
      ) : (
        <DealList
          deals={flatDeals}
          stages={stages}
          onOpenDeal={(d) => nav(`/deals/${d.deal_id}`)}
          tab={activeTab}
          hasMore={flatHasMore}
          loading={flatLoading}
          onLoadMore={() => loadFlat(true)}
        />
      )}

      {/* Modals */}
      {showNewDeal && (
        <NewDealModal
          pipelines={pipelines}
          stages={stages}
          pipelineId={pipelineId}
          onClose={() => setShowNewDeal(false)}
          onCreated={(d) => {
            setShowNewDeal(false);
            refreshDeals();
            refreshSummary();
            nav(`/deals/${d.deal_id}`);
          }}
        />
      )}
      {moveDeal && (
        <MoveStageModal
          deal={moveDeal.deal}
          fromStage={stages.find((s) => s.stage_id === moveDeal.deal.current_stage_id)}
          toStage={moveDeal.toStage}
          onCancel={() => setMoveDeal(null)}
          onConfirm={submitMove}
        />
      )}
    </div>
  );
}

function KanbanBoard({ stages, columnData, stageCounts, onLoadMoreColumn, onDragStart, onDragOver, onDrop, onOpenDeal, viewMode }) {
  const term = useTerm();
  return (
    <div className="mt-6 overflow-x-auto pb-6" data-testid="kanban-board">
      <div className="flex gap-4 min-w-max">
        {stages.map((s) => {
          const col = columnData[s.stage_id] || { items: [], loading: true, hasMore: false, error: null };
          const count = stageCounts[s.stage_id] ?? col.items.length;
          return (
            <KanbanColumn
              key={s.stage_id}
              stage={s}
              col={col}
              count={count}
              viewMode={viewMode}
              onLoadMore={() => onLoadMoreColumn(s.stage_id)}
              onDragStart={onDragStart}
              onDragOver={onDragOver}
              onDrop={onDrop}
              onOpenDeal={onOpenDeal}
            />
          );
        })}
      </div>
    </div>
  );
}

function KanbanColumn({ stage, col, count, viewMode, onLoadMore, onDragStart, onDragOver, onDrop, onOpenDeal }) {
  const term = useTerm();
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !col.hasMore || col.loading) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) onLoadMore();
    }, { rootMargin: "150px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [col.hasMore, col.loading, onLoadMore]);

  return (
    <div
      className="cw-kanban-column"
      onDragOver={onDragOver}
      onDrop={(e) => onDrop(e, stage.stage_id)}
      data-testid={`stage-column-${stage.stage_name}`}
    >
      <div className="flex items-center justify-between mb-3 px-1">
        <div className="font-semibold text-sm" style={{ color: "var(--cw-primary-dark)" }}>
          {stage.stage_name}
        </div>
        <span
          className="text-xs font-semibold rounded-full px-2 py-0.5"
          style={{ background: "rgba(40,42,116,0.1)", color: "var(--cw-primary)" }}
          data-testid={`stage-count-${stage.stage_name}`}
        >
          {count}
        </span>
      </div>
      <div>
        {col.loading && col.items.length === 0 && !col.error && (
          <div className="space-y-2 py-2">
            <SkeletonRows rows={3} />
          </div>
        )}
        {col.error && col.items.length === 0 && (
          <div className="text-xs text-[var(--cw-error)] italic py-4 text-center">
            {col.error}
          </div>
        )}
        {col.items.map((d) => (
          <DealCard
            key={d.deal_id}
            deal={d}
            onDragStart={onDragStart}
            onClick={() => onOpenDeal(d)}
            viewMode={viewMode}
          />
        ))}
        {!col.loading && !col.error && col.items.length === 0 && (
          <div className="text-xs text-[var(--cw-muted)] italic py-4 text-center">
            No {term("deals")}
          </div>
        )}
        {col.hasMore && col.items.length > 0 && (
          <div
            ref={sentinelRef}
            className="py-2 text-center text-[11px] text-[var(--cw-muted)]"
            data-testid={`column-sentinel-${stage.stage_name}`}
          >
            {col.loading
              ? <span className="inline-flex items-center gap-1"><Loader2 size={10} className="animate-spin" /> Loading…</span>
              : "Scroll to load more"}
          </div>
        )}
      </div>
    </div>
  );
}

function DealCard({ deal, onDragStart, onClick, viewMode = "detailed" }) {
  const daysInStage = deal.days_in_stage ?? daysBetween(deal.stage_entered_at);
  const daysOld = deal.days_old ?? daysBetween(deal.created_at);
  const initials = (deal.deal_owner_name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase())
    .join("");
  const tagBadges = null;  // v5.14: FF/Interesting now render inside DealTagsBar on Deal Detail; card no longer needs hardcoded chips
  const agingChips = (
    <div className="flex flex-wrap gap-1.5" data-testid={`deal-aging-${deal.company_name}`}>
      <span
        className="text-[10px] font-medium px-1.5 py-0.5 rounded"
        style={{
          background: deal.is_stagnant ? "rgba(215,60,60,0.12)" : "rgba(40,42,116,0.08)",
          color: deal.is_stagnant ? "#B02B2B" : "var(--cw-primary)",
        }}
        title={deal.is_stagnant ? "Stagnant (past stage threshold)" : undefined}
      >
        {daysInStage ?? 0}d in stage
      </span>
      <span
        className="text-[10px] font-medium px-1.5 py-0.5 rounded"
        style={{ background: "rgba(0,0,0,0.05)", color: "var(--cw-muted)" }}
      >
        {daysOld ?? 0}d old
      </span>
    </div>
  );
  const ownerAvatar = initials ? (
    <span
      className="inline-flex items-center justify-center h-5 w-5 rounded-full text-[10px] font-semibold"
      style={{ background: "var(--cw-secondary)", color: "var(--cw-primary-dark)" }}
      title={`Owner: ${deal.deal_owner_name}`}
      data-testid={`owner-avatar-${deal.company_name}`}
    >
      {initials}
    </span>
  ) : (
    <span className="text-[10px] italic">No owner</span>
  );

  // v5.7 Addition 11: Quick View — company + tag badge + both aging chips + owner
  if (viewMode === "quick") {
    return (
      <div
        className="cw-deal-card"
        draggable
        onDragStart={(e) => onDragStart(e, deal)}
        onClick={onClick}
        data-testid={`deal-card-${deal.company_name}`}
      >
        <div className="flex items-start justify-between gap-2">
          <div className="font-semibold text-sm text-[var(--cw-primary-dark)] truncate">
            {deal.company_name}
          </div>
          {tagBadges}
        </div>
        <div className="mt-2 flex items-center justify-between gap-2">
          {agingChips}
          {ownerAvatar}
        </div>
      </div>
    );
  }

  // Detailed View (unchanged)
  return (
    <div
      className="cw-deal-card"
      draggable
      onDragStart={(e) => onDragStart(e, deal)}
      onClick={onClick}
      data-testid={`deal-card-${deal.company_name}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-semibold text-sm text-[var(--cw-primary-dark)] truncate">
          {deal.company_name}
        </div>
        {tagBadges}
      </div>
      {deal.sector && (
        <div className="text-xs text-[var(--cw-muted)] mt-1">{deal.sector}</div>
      )}
      <div className="mt-3">
        <div className="text-[10px] uppercase text-[var(--cw-muted)] tracking-wider">
          Round
        </div>
        <div className="text-xs font-medium truncate">
          {deal.round_size || "—"}
        </div>
      </div>
      <div className="mt-3">{agingChips}</div>
      <div
        className="mt-3 pt-2 border-t text-[11px] text-[var(--cw-muted)] flex justify-between items-center"
        style={{ borderColor: "var(--cw-border)" }}
      >
        <span>{formatDay(deal.created_at)}</span>
        {ownerAvatar}
      </div>
    </div>
  );
}

function DealList({ deals, stages, onOpenDeal, tab, hasMore, loading, onLoadMore }) {
  const term = useTerm();
  const sentinelRef = useRef(null);
  useEffect(() => {
    if (!sentinelRef.current || !hasMore || loading) return;
    const obs = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) onLoadMore?.();
    }, { rootMargin: "200px" });
    obs.observe(sentinelRef.current);
    return () => obs.disconnect();
  }, [hasMore, loading, onLoadMore]);

  if (deals.length === 0 && !loading) {
    return (
      <div className="mt-8 cw-card p-10 text-center text-[var(--cw-muted)]">
        No {term("deals")} in this view.
      </div>
    );
  }
  const stageName = (id) => stages.find((s) => s.stage_id === id)?.stage_name || "—";
  return (
    <div className="mt-6 cw-card overflow-hidden" data-testid={`deal-list-${tab}`}>
      <table className="w-full text-sm">
        <thead style={{ background: "rgba(40,42,116,0.05)" }}>
          <tr className="text-left">
            <th className="px-4 py-3 font-semibold">Company</th>
            <th className="px-4 py-3 font-semibold">Sector</th>
            <th className="px-4 py-3 font-semibold">{term("Stage")}</th>
            <th className="px-4 py-3 font-semibold">Round</th>
            <th className="px-4 py-3 font-semibold">Updated</th>
          </tr>
        </thead>
        <tbody>
          {loading && deals.length === 0 && (
            <tr><td colSpan={5} className="p-4"><SkeletonRows rows={5} /></td></tr>
          )}
          {deals.map((d) => (
            <tr
              key={d.deal_id}
              className="border-t cursor-pointer hover:bg-[rgba(40,42,116,0.04)]"
              style={{ borderColor: "var(--cw-border)" }}
              onClick={() => onOpenDeal(d)}
              data-testid={`deal-row-${d.company_name}`}
            >
              <td className="px-4 py-3 font-medium">{d.company_name}</td>
              <td className="px-4 py-3 text-[var(--cw-muted)]">{d.sector || "—"}</td>
              <td className="px-4 py-3">{stageName(d.current_stage_id)}</td>
              <td className="px-4 py-3">{d.round_size || "—"}</td>
              <td className="px-4 py-3 text-[var(--cw-muted)]">{formatDay(d.updated_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      {hasMore && deals.length > 0 && (
        <div
          ref={sentinelRef}
          className="py-3 text-center text-xs text-[var(--cw-muted)]"
          data-testid={`deal-list-sentinel-${tab}`}
        >
          {loading
            ? <span className="inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Loading more…</span>
            : "Scroll to load more"}
        </div>
      )}
    </div>
  );
}

/* -------------------- v5.11 A2.1 — New Deal dropdown -------------------- */
function NewDealDropdown({ pipelineId, onManual }) {
  const t = useTerm();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const pickDeck = () => {
    setOpen(false);
    inputRef.current?.click();
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!/\.pdf$/i.test(f.name)) {
      toast.error("Upload a PDF (PPTX support coming soon)");
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error("File must be under 50MB");
      return;
    }
    setBusy(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        try {
          const base64 = String(reader.result).split(",")[1] || "";
          const { data } = await api.post("/new-deal/from-deck", {
            filename: f.name,
            file_type: "pdf",
            data_base64: base64,
            pipeline_id: pipelineId,
          });
          toast.success("Extraction started");
          nav(`/agent-runs/${data.run_id}/map`);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Failed to start extraction");
        } finally { setBusy(false); }
      };
      reader.readAsDataURL(f);
    } catch { setBusy(false); }
  };

  return (
    <div className="relative">
      <button className="cw-btn-primary flex items-center gap-2"
        onClick={() => setOpen((v) => !v)}
        data-testid="new-deal-btn">
        <Plus size={16} /> New {t("Deal")}
      </button>
      <input ref={inputRef} type="file" accept="application/pdf" hidden onChange={onFile} />
      {open && (
        <div className="absolute right-0 mt-2 rounded-md border bg-white shadow-lg z-10 min-w-[240px]"
          style={{ borderColor: "var(--cw-border)" }}
          data-testid="new-deal-menu">
          <button onClick={() => { setOpen(false); onManual(); }}
            className="w-full text-left px-4 py-2 text-sm hover:bg-[rgba(40,42,116,0.05)]"
            data-testid="new-deal-scratch">
            <div className="font-medium">Start from scratch</div>
            <div className="text-xs text-[var(--cw-muted)]">Fill the form manually</div>
          </button>
          <div className="border-t" style={{ borderColor: "var(--cw-border)" }}></div>
          <button onClick={pickDeck} disabled={busy}
            className="w-full text-left px-4 py-2 text-sm hover:bg-[rgba(40,42,116,0.05)] disabled:opacity-50"
            data-testid="new-deal-upload-deck">
            <div className="font-medium">Upload a deck</div>
            <div className="text-xs text-[var(--cw-muted)]">Gemini extracts fields for review</div>
          </button>
        </div>
      )}
    </div>
  );
}

